

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import FirebasePrintJobRepository from '../../../components/repository/FirebasePrintJobRepository';
import MerchantPrinter from '../../../components/merchant/components/Util/MerchantPrinter';
import { PrinterType } from '../../../Model/Printer';
import { FailSetPrintedError } from './PrintService/PrintServiceError';


export const PrintJobStatus = {
    newJob: 1,
    printing: 2,
    printed: 3,
    error: 4,
    stopped: 5,
    removing: 6,
}

export const printJobStatusText = (printJobStatus) => {
    let map = {
        [PrintJobStatus.newJob]: lang.new,
        [PrintJobStatus.printing]: lang.printing,
        [PrintJobStatus.printed]: lang.printed,
        [PrintJobStatus.error]: lang.error,
        [PrintJobStatus.stopped]: lang.printing_stopped,
        [PrintJobStatus.removing]: lang.removing,
    }
    return map[printJobStatus]
}


// For some reason if job failed to print, we do exponential backoff
// instead of keep spamming the same failed job without giving time
// for other correction method to response
let _jobDelay = {}

class BasePrintService {
    isConnected = false
    pendingJobList = []
    processNextJobTimer = null
    lastActivity = null
    app = null

    printerType = {
        PRINTER_TYPE_NETWORK                : "network",
        PRINTER_TYPE_USB                    : "usb",
        PRINTER_TYPE_FEIEYUN                : "feieyun",
        PRINTER_TYPE_DEVICE_CUSTOM_PRINTER  : "device-custom-printer",
        PRINTER_TYPE_SUNMI                  : "sunmi",
    }

    init(app) {
        this.app = app
    }
    start = () => {
        this.lastActivity = moment()
        
        if (!this.isConnected) {
            console.log(`${this.constructor.name} start()`)
            this.isConnected = true
            this.#connectFirebase()
        }
    }
    stop = () => {
        this.getPrintJobRepository().removeCallback(null, this)
        this.pendingJobList.length = 0
        this.#stopPrintLoop()
        this.isConnected = false

        if (this.healthCheckTimer) {
            clearTimeout(this.healthCheckTimer)
            this.healthCheckTimer = null
        }
    }

    getPrintJobRepository = () => {
        return FirebasePrintJobRepository.getInstance()
    }

    #connectFirebase = () => {
        this.getPrintJobRepository().onChildAdded((printJob) => {
            console.log("connectFirebase", printJob)
            if (printJob && !Array.isArray(printJob)) {
                printJob = [printJob]
            }
            printJob.forEach(pj => {
                pj.printFailCount = pj.printFailCount || 0
                pj.status = PrintJobStatus.newJob
                this.pendingJobList.push(pj)
            })
            this.#notifyJobListChange()
    
            setTimeout(() => {
                // 1 second buffer before start to print
                this.#processNextJob()
            }, 1000);
        }, this.constructor.name, this)
    
        this.getPrintJobRepository().onChildRemoved((jobRemoved) => {
            if (jobRemoved && !Array.isArray(jobRemoved)) {
                jobRemoved = [jobRemoved]
            }
            jobRemoved.forEach(printJob => {
                let index = this.pendingJobList.findIndex(item => item.id == printJob.id)
                if (index !== -1) {
                    this.pendingJobList.splice(index, 1)
                }
            })
            this.#notifyJobListChange()
        }, this.constructor.name, this)
        this.#startPrintLoop()
    }

    healthCheckTimer = null
    #startPrintLoop = () => {

        if (!this.healthCheckTimer) {
            this.healthCheckTimer = setInterval(() => {
                if (moment().diff(this.lastActivity, 'second') > 30) {
                    this.#resetTimerAndForceProcessNextJob()
                }
            }, 30000);
        }

        this.#processNextJob()
    }

    #stopPrintLoop = () => {
        if (this.healthCheckTimer) {
            clearTimeout(this.healthCheckTimer)
        }
        this.healthCheckTimer = null
        if (this.processNextJobTimer) {
            clearTimeout(this.processNextJobTimer)
        }
        this.processNextJobTimer = null
    }

    #notifyJobListChange = () => {
        if (this.jobListChangeThrottleTimer) {
            this.hasNewData = true
            return
        }

        // Notify all listeners
        Object.keys(this.jobListListeners).forEach(id => {
            this.jobListListeners[id](this.pendingJobList)
        })

        // We have done notified listeners on the latest data
        this.hasNewData = false

        // Throttle to wait for at least N interval before notifying
        // listeners again
        let throttleInterval = 10000
        this.jobListChangeThrottleTimer = setTimeout(() => {
            // Reset timer, so that can immediately notify listeners again
            this.jobListChangeThrottleTimer = null

            if (this.hasNewData) {
                // We has new data to be notified to listeners during the throttle
                // period, notify them now
                this.#notifyJobListChange()
            }
        }, throttleInterval);
    }

    #resetTimerAndForceProcessNextJob = (options) => {
        if (this.processNextJobTimer) {
            clearTimeout(this.processNextJobTimer)
        }
        this.processNextJobTimer = setTimeout(() => {
            this.processNextJobTimer = null
            this.#processNextJob()
        }, options && options.delay || 100)
    }

    _sendToFlutterPrint = (printJob, printer) => {
        if (printJob.retry_after && moment().isBefore(printJob.retry_after)) {
            // Done process job (regardless of success or failure),
            // give printer some time to complete the print job and 
            // proceed to process next job after 2 sec
            this.#resetTimerAndForceProcessNextJob({delay: 2000})
            return 
        }
        printJob.status = PrintJobStatus.printing
        this.#notifyJobListChange()
    
        printJob.printAttempt = (printJob.printAttempt || 0) + 1
        MerchantPrinter.flutterPrintRawContent(printJob.content, printer)
            .then((res) => {
                if (res.status) {
                    printJob.status = PrintJobStatus.printed
                    this.#notifyJobListChange()
                    return this.getPrintJobRepository().setJobPrinted(printJob.id)
                        .catch(err => {
                            throw new FailSetPrintedError(err)
                        })
                }
    
                if (printJob.status != PrintJobStatus.removing) {
                    // Exponential backoff starting at 10 second delay, max 60 second
                    printJob.status = PrintJobStatus.error
                    printJob.exponential_backoff = Math.min(printJob.exponential_backoff || 1, 15) * 2
                    printJob.retry_after = moment().add(printJob.exponential_backoff, 'seconds')
                    console.log(`Exponential backoff for job: ${printJob.id}, next retry at ${printJob.exponential_backoff} seconds`)    
                }
    
                printJob.error_message = res.trace || ''
                this.#notifyJobListChange()
                throw "Fail to print"
            })
            .then(() => {
                // Update job as printed, then add some delay before remove the job,
                // for UI purpose only
    
                printJob.status = PrintJobStatus.printed
                this.#notifyJobListChange()
    
    
                const printJobId = printJob.id
    
                setTimeout(() => {
                    let index = this.pendingJobList.findIndex(pj => pj.id == printJobId)
                    // Pop the processed job
                    if (index > -1) {
                        this.pendingJobList.splice(index, 1)
                        this.#notifyJobListChange()
                    }    
                }, 5000);
            })
            .catch(err => {
                printJob.printFailCount++
                if (printJob.printFailCount >= 5) {
                    printJob.status = PrintJobStatus.stopped
                }
                this.#notifyJobListChange()
                if (err) {
                    console.log(err)
                }
            })
            .finally(() => {
                // Done process job (regardless of success or failure),
                // give printer some time to complete the print job and 
                // proceed to process next job after 2 sec
                this.#resetTimerAndForceProcessNextJob({delay: 2000})
            })
        return
    }

    jobListListeners = {}

    /**
     * Add listener to get notified when job list is updated
     * @param {Function} callback
     * @returns 
     */
    addJobListListener = (callback) => {
        let id = Math.random()
        this.jobListListeners[id] = callback

        // Send current data to callback function, else
        // user will not get any data for UI display until
        // next tick
        callback(this.pendingJobList)
        return id
    }

    removeJobListListener = (id) => {
        if (this.jobListListeners[id]) {
            delete this.jobListListeners[id]
        }
    }

    clearAllPrintJob = () => {
        if (this.pendingJobList && this.pendingJobList.length) {
            this.getPrintJobRepository().setJobPrinted(this.pendingJobList.map(pj => {
                pj.status = PrintJobStatus.removing
                return pj.id
            }), "Manual removed")
        }
        this.#notifyJobListChange()
    }

    #processNextJob = () => {
        if (!window.flutter_inappwebview) {
            return
        }
        if (this.processNextJobTimer) {
            // Already has a scheduled job, wait
            return
        }
    
        this.lastActivity = moment()
        if (this.pendingJobList.length == 0) {
            // No job, clear time and prepare to start job immediately on new job arrive
            clearTimeout(this.processNextJobTimer)
            this.processNextJobTimer = null
            return
        }
        else {
            this.processNextJobTimer = setTimeout(() => {
                // Start a long timer to lock from double processing job,
                // but not too long that will auto reset if job processing stuck/stop.
                // No worry, if job is processed successfully, this timer will be removed
                this.#resetTimerAndForceProcessNextJob()
            }, 30000);
        }
    
        const filterByJobStatus = (pj) => {
            return [PrintJobStatus.newJob, PrintJobStatus.error].indexOf(pj.status) !== -1
        }
        let printJobIndex = this.pendingJobList.findIndex(pj => filterByJobStatus(pj) && (!pj.retry_after || moment().isAfter(pj.retry_after)) && pj.printFailCount < 5 )
        let printJob = null // pendingJobList[0]
        if (printJobIndex > -1) {
            printJob = this.pendingJobList[printJobIndex]
        }
    
        let isUnableToProcessJob = false
        if (printJob) {
            // Unable to "return" from this block, mean unable to handle the job
            isUnableToProcessJob = true
            if (this.customProcessPrintJob(printJob)) {
                return
            }
            else if (printJob.printer_type == this.printerType.PRINTER_TYPE_DEVICE_CUSTOM_PRINTER && printJob.content) {
                const customReceiptPrinter = this.app.state.customReceiptPrinter
                if (customReceiptPrinter && customReceiptPrinter.generalType === PrinterType.BLUETOOTH) {
                    this._sendToFlutterPrint(printJob)
                    return
                }
            }
            else if (printJob.printer_type === this.printerType.PRINTER_TYPE_NETWORK && printJob.ip) {
                this._sendToFlutterPrint(printJob, {
                    ip_address: printJob.ip,
                })
                return
            }
            else if (printJob.printer_type === this.printerType.PRINTER_TYPE_USB) {
                this._sendToFlutterPrint(printJob, {
                    printer_type: 'usb',
                })
                return
            }
        }
        
        
        if (isUnableToProcessJob) {
            // Unable to process this job for some reason
            // move this job to end of queue to prevent blocking other jobs
            let temp = this.pendingJobList.splice(printJobIndex, 1)
            this.pendingJobList.push(temp)
        } 
        
        console.log("Delay 100 milisecond before process next job")
        this.#resetTimerAndForceProcessNextJob({delay: 100})
    }

    customProcessPrintJob() {
        // Override this function to define your own logic
        return false
    }
}

BasePrintService.propTypes = {

};

export default BasePrintService;